import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import API_ROUTES_V2 from "~/api-routes-v2";
import { fetchServices } from "~/components/global-entities/globalEntitiesSlice";
import apiService from "~/services/service";
import z from "zod";
import { ApiResponse } from "../../types/tenants";

const useTenantCustomerPortfolio = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mappedServices, setMappedServices] = useState([]);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [processTypes, setProcessTypes] = useState([]);
  const [serviceData, setServiceData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const { id } = useParams();
  const contextMenu = useRef({});
  const dispatch = useDispatch();
  useEffect(() => {
    const loadServiceData = async () => {
      try {
        const procTypes = await fetchProcessTypes();
        setProcessTypes(procTypes || []);

        const servicesResponse = await dispatch(fetchServices()).unwrap();
        const svc = servicesResponse?.data ?? servicesResponse;
        setServiceData(svc || []);
      } catch (err) {
        console.error("Static load error", err);
      }
    };

    loadServiceData();
  }, []);

  const fetchProcessTypes = async () => {
    try {
      const { data } = await apiService(API_ROUTES_V2.FETCH_PROCESS_TYPE);
      return data;
    } catch (error) {
      console.error("Error fetching process types:", error);
    }
  };
  const fetchTenantEntities = async ({
    pageIndex = page,
    pageSize = rows,
    sortBy,
    sortOrder,
    skipCache = false,
  }) => {
    try {
      setLoading(true);

      const response = await apiService(
        API_ROUTES_V2.FETCH_TENANT_ENTITY_ACCESS,
        "GET",
        {
          skipCache,
          replace: {
            id,
            page: pageIndex + 1,
            pageSize,
            sortBy: sortBy || "name",
            sortOrder: sortOrder || "asc",
          },
          schema: z.object({
            id: z.string().uuid(),
            page: z.number().optional(),
            pageSize: z.number().optional(),
            sortBy: z.enum(["name", "expiry"]).optional(),
            sortOrder: z.enum(["asc", "desc"]).optional(),
          }),
        },
      );

      const tenantData = response?.data?.entityAccess || [];
      setTotalRecords(response?.data?.pagination?.total || 0);
      setData(tenantData);

      if (processTypes.length && serviceData.length) {
        const accessible = serviceData.map((service) => ({
          id: service.id,
          name: service.name,
          processes: processTypes.filter((proc) => proc.service === service.id),
        }));

        setMappedServices(accessible);
      }
    } catch (err) {
      console.error("Tenant fetch error", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!processTypes.length || !serviceData.length) return;
    fetchTenantEntities({ pageIndex: page, pageSize: rows, sortBy, sortOrder });
  }, [id, processTypes, serviceData]);

  const treeData = useMemo(() => {
    if (!data.length || !mappedServices.length) return [];

    return data.map((tenant, tenantIndex) => {
      const serviceLines =
        tenant.services?.map(({ serviceId }) =>
          mappedServices.find((s) => s.id === serviceId),
        ) || [];
      return {
        key: `tenant-${tenantIndex}`,
        data: {
          name: tenant.entityName,
          expirationDate: tenant.expiresAt || null,
          level: "entity",
          country: tenant.country,
          id: tenant.entityId,
          services: tenant.services,
        },

        children: serviceLines.map((sl, slIndex) => ({
          key: `tenant-${tenantIndex}-line-${slIndex}`,
          data: {
            name: sl?.name || "Unknown Service",
            level: "serviceline",
          },
          children:
            sl?.processes
              ?.filter((proc) =>
                tenant.services
                  ?.find((s) => s.serviceId === sl.id)
                  ?.processCategories?.includes(proc.process_category),
              )
              .map((proc, srvIndex) => ({
                key: `tenant-${tenantIndex}-line-${slIndex}-srv-${srvIndex}`,
                data: {
                  name: proc.name,
                  level: "service",
                  processCategory: proc.process_category,
                },
                leaf: true,
              })) ?? [],
        })),
      };
    });
  }, [data, mappedServices]);
  const handleChangePage = (e) => {
    const newPage = e.page;
    const newRows = e.rows;

    setPage(newPage);
    setRows(newRows);

    fetchTenantEntities({ pageIndex: newPage, pageSize: newRows });
  };
  const onSortChange = (e) => {
    setSortBy(e.sortField);
    setSortOrder(e.sortOrder);
    fetchTenantEntities({
      sortBy: e.sortField,
      sortOrder: e.sortOrder === -1 ? "desc" : "asc",
    });
  };

  const deleteLspEntityAccess = async (
    lspTenantId: string,
    entityId: string,
  ) => {
    try {
      const response: ApiResponse = await apiService(
        API_ROUTES_V2.REMOVE_TENANT_ENTITY_ACCESS,
        "DELETE",
        {
          suppressToast: true,
          replace: { lspTenantId, entityId },
        },
      );
      return response;
    } catch (error) {
      throw new Error("Failed to delete entity access");
    }
  };
  return {
    data,
    setData,
    loading,
    setLoading,
    mappedServices,
    setMappedServices,
    contextMenu,
    treeData,
    page,
    rows,
    totalRecords,
    onPageChange: handleChangePage,
    onSortChange,
    sortBy,
    sortOrder,
    deleteLspEntityAccess,
    fetchTenantEntities,
  };
};

export default useTenantCustomerPortfolio;
